import React from 'react';
import PropTypes from 'prop-types';
import { Grid, TextField, FormHelperText, Box, FormControl } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { CardNumberElement, CardExpiryElement, CardCvcElement } from '@stripe/react-stripe-js';
import StripeElement from 'components/StripeElement';
import { getCountries } from 'utils/countries';
import WhatsappPhoneTextField from 'components/WhatsappPhoneTextField';
import { useStyles } from './styles';

const Props = {
  data: PropTypes.shape({
    name: PropTypes.string.isRequired,
    line1: PropTypes.string.isRequired,
    city: PropTypes.string.isRequired,
    state: PropTypes.string.isRequired,
    postalCode: PropTypes.string.isRequired,
    country: PropTypes.string.isRequired,
  }),
  errorFields: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  hiddenFields: PropTypes.array,
};

export default function CardFormFields({ data, errorFields, onChange, hiddenFields }) {
  const classes = useStyles();

  return (
    <>
      <Grid container={true} spacing={1}>
        <Grid item={true} xs={12} md={8}>
          <div className={classes.inputCardNumber}>
            <span>Card Number</span>
            <span className="highlight">*</span>
            <StripeElement
              element={CardNumberElement}
              error={errorFields.includes('numberCompleted')}
              onChange={(event) => onChange('numberCompleted', event.complete)}
            />
          </div>

          {errorFields.includes('numberCompleted') && (
            <FormHelperText variant="outlined" error={true}>
              Please fill this field correctly
            </FormHelperText>
          )}
        </Grid>

        <Grid item={true} xs={12} md={2}>
          <div className={classes.inputCardNumber}>
            <span>Expiration Date</span>
            <span className="highlightDate">*</span>
            <StripeElement
              element={CardExpiryElement}
              error={errorFields.includes('expiryCompleted')}
              onChange={(event) => onChange('expiryCompleted', event.complete)}
            />
          </div>

          {errorFields.includes('expiryCompleted') && (
            <FormHelperText variant="outlined" error={true}>
              Please fill this field correctly
            </FormHelperText>
          )}
        </Grid>

        <Grid item={true} xs={12} md={2}>
          <div className={classes.inputCardNumber}>
            <span>CVC</span>
            <span className="highlightCVC">*</span>
            <StripeElement
              element={CardCvcElement}
              error={errorFields.includes('cvcCompleted')}
              onChange={(event) => onChange('cvcCompleted', event.complete)}
            />
          </div>

          {errorFields.includes('cvcCompleted') && (
            <FormHelperText variant="outlined" error={true}>
              Please fill this field correctly
            </FormHelperText>
          )}
        </Grid>

        <Grid item={true} xs={12} sm={12}>
          <FormControl variant="standard" fullWidth={true} margin="dense">
            <TextField
              fullWidth={true}
              variant="outlined"
              error={errorFields.includes('name')}
              helperText={
                errorFields.includes('name') ? 'Please fill this field correctly' : undefined
              }
              label={
                <>
                  Name<span className={classes.colorMandatory}>*</span>
                </>
              }
              placeholder=""
              InputLabelProps={{
                shrink: true,
              }}
              value={data.name}
              onChange={(event) => onChange('name', event.target.value)}
            />
          </FormControl>
        </Grid>

        <Grid item={true} xs={12}>
          <FormControl variant="standard" fullWidth={true} margin="dense">
            <TextField
              fullWidth={true}
              variant="outlined"
              error={errorFields.includes('line1')}
              helperText={
                errorFields.includes('line1') ? 'Please fill this field correctly' : undefined
              }
              label={
                <>
                  Address<span className={classes.colorMandatory}>*</span>
                </>
              }
              placeholder=""
              InputLabelProps={{
                shrink: true,
              }}
              value={data.line1}
              onChange={(event) => onChange('line1', event.target.value)}
            />
          </FormControl>
        </Grid>

        <Grid item={true} xs={6} sm={4}>
          <FormControl variant="standard" fullWidth={true} margin="dense">
            <TextField
              fullWidth={true}
              variant="outlined"
              error={errorFields.includes('city')}
              helperText={
                errorFields.includes('city') ? 'Please fill this field correctly' : undefined
              }
              label={
                <>
                  City<span className={classes.colorMandatory}>*</span>
                </>
              }
              placeholder=""
              InputLabelProps={{
                shrink: true,
              }}
              value={data.city}
              onChange={(event) => onChange('city', event.target.value)}
            />
          </FormControl>
        </Grid>

        <Grid item={true} xs={6} sm={2}>
          <FormControl variant="standard" fullWidth={true} margin="dense">
            <TextField
              fullWidth={true}
              variant="outlined"
              error={errorFields.includes('state')}
              helperText={
                errorFields.includes('state') ? 'Please fill this field correctly' : undefined
              }
              label={
                <>
                  State/Region<span className={classes.colorMandatory}>*</span>
                </>
              }
              placeholder=""
              InputLabelProps={{
                shrink: true,
              }}
              value={data.state}
              onChange={(event) => onChange('state', event.target.value)}
            />
          </FormControl>
        </Grid>

        <Grid item={true} xs={6} sm={3}>
          <FormControl variant="standard" fullWidth={true} margin="dense">
            <TextField
              fullWidth={true}
              variant="outlined"
              error={errorFields.includes('postalCode')}
              helperText={
                errorFields.includes('postalCode') ? 'Please fill this field correctly' : undefined
              }
              label="Postal Code"
              placeholder=""
              InputLabelProps={{
                shrink: true,
              }}
              value={data.postalCode}
              onChange={(event) => onChange('postalCode', event.target.value)}
            />
          </FormControl>
        </Grid>

        <Grid item={true} xs={6} sm={3}>
          <FormControl variant="standard" fullWidth={true} margin="dense">
            <Autocomplete
              options={['', ...getCountries()]}
              value={data.country === 'Taiwan, Province of China' ? 'Taiwan' : data.country}
              onChange={(event, value) => onChange('country', value || '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth={true}
                  variant="outlined"
                  error={errorFields.includes('country')}
                  helperText={
                    errorFields.includes('country') ? 'Please fill this field correctly' : undefined
                  }
                  label={
                    <>
                      Country<span className={classes.colorMandatory}>*</span>
                    </>
                  }
                  placeholder=""
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              )}
            />
          </FormControl>
        </Grid>

        {!hiddenFields?.includes('whatsappNumber') && (
          <Box pt={2} ml={0.5} width="100%">
            <WhatsappPhoneTextField
              variant="outlined"
              label="Whatsapp Number"
              value={data.whatsappNumber}
              onChange={(value) => onChange('whatsappNumber', value)}
              error={errorFields.includes('whatsappNumber')}
              helperText={
                errorFields.includes('whatsappNumber')
                  ? 'Please fill this field correctly'
                  : undefined
              }
            />
          </Box>
        )}
      </Grid>
    </>
  );
}

CardFormFields.propTypes = Props;
